import { useEffect, useRef, useState } from "react"
import { ReactReader, IReactReaderStyle } from "react-reader"

import { ThemeConjunction } from "./Theme";

import './index.scss';
import { NavItem, Rendition } from "epubjs";
import { useReaderSetting } from "../../../../../../Recoil/Novel/ReaderSetting";
import { useReaderSize } from "../../../../../../Recoil/Novel/ReaderSize";
import { randomString } from "../../../../../../Functions";

const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const EpubViewer = ({dataUrl}:{ dataUrl: string}) => {

    const {setting} = useReaderSetting();
    const rendition = useRef<Rendition | undefined>(undefined);
    const [location, setLocation] = useState<string | number | null>(null);
    const [readerKey,] = useState(randomString(32));

    const commonStyles = {
        arrow: {
            display: 'none',
        },
        container: {
            padding: "60px 0"
        },
    } as IReactReaderStyle;
    
    const updateTheme = (rendition: Rendition, setting: displaySettings) => {
        switch (setting.theme) {
            case 'dark': 
                rendition.themes.override('color', '#fff');
                rendition.themes.override('background', 'rgb(26,28,30)');
                break;
            case 'light': 
                rendition.themes.override('color', '#000');
                rendition.themes.override('background', '#fff');
                break;
        }
    }

    useEffect(() => {
        (async()=>{
            while (!rendition.current) {
                await wait(50);
            }

            updateTheme(rendition.current, setting);
            rendition.current.themes.fontSize(setting.fontSize);
        })();
    },[dataUrl, setting]);

    return (
        <div className={`ReaderArea ${setting.theme}Theme ${setting.type}`} >
            <ReactReader
                key={readerKey}
                location={location}
                locationChanged={(loc: string) => setLocation(loc)}
                url={dataUrl}
                epubOptions={{
                    flow: "scrolled",
                    manager: "default",
                    allowScriptedContent: false
                }}
                showToc={false}
                readerStyles={ThemeConjunction(commonStyles, setting.theme)}
                getRendition={(_rendition) => {
                    rendition.current = _rendition;

                    updateTheme(rendition.current, setting);
                    rendition.current.themes.fontSize(setting.fontSize);

                    _rendition.on('started', () => {
                        _rendition.book.navigation.toc = _rendition.book.navigation.toc.filter((item: NavItem) => {
                            return !item.href.includes("cover") && !item.href.includes("copyright");
                        });
                    });

                    const spine_get = _rendition.book.spine.get.bind(_rendition.book.spine);  
                    _rendition.book.spine.get = (target) => {
                        let ret = spine_get(target);
                        
                        let firstNonCoverNonCopyRight = (_rendition.book.spine as any).items.find((item: NavItem) => {
                            return !item.href.includes("cover") && !item.href.includes("copyright");
                        });

                        if (!ret) {
                            target = firstNonCoverNonCopyRight.href;
                            return spine_get(target);
                        }

                        if (typeof target === 'string') {
                            if (target.includes("cover") || target.includes("copyright")) {
                                target = firstNonCoverNonCopyRight.href;
                                ret = spine_get(target);
                            }
                        }

                        return ret;
                    }
                }}
            />
            <ReadCover />
        </div>
    )
};

const ReadCover = () => {

    const readerCoverRef = useRef<HTMLDivElement>(null);
    const {setPage} = useReaderSize();

    useEffect(()=>{
        const resizeObserver = new ResizeObserver((entries) => {
            
            for (let entry of entries) {
                let rect = entry.target.getBoundingClientRect();

                setPage(page => ({
                    ...page,
                    total: Math.ceil(rect.height / window.innerHeight),
                    readerYRange: [rect.top + window.scrollY, rect.bottom + window.scrollY]
                })); 
            }
        });
        resizeObserver.observe(readerCoverRef.current as Element);

        return () => {
            resizeObserver.disconnect();
        }
    },[setPage])


    return <div ref={readerCoverRef} style={{position: 'absolute', width: "100%", height: "100%", top: 0, left: 0}} />;
}

export default EpubViewer;